<template>
  <tr>
    <td :align="contentModel.style.alignment" :style="rootStyles">
      <table border="0" cellpadding="0" cellspacing="0" role="presentation">
        <tr>
          <template v-if="!isBuilder">
            <td
              align="center"
              :bgcolor="contentModel.style.color"
              role="presentation"
              :style="contentStyles"
              valign="middle"
            >
              <a
                :style="anchorStyles"
                :target="contentModel.target"
                :href="contentModel.href"
              >
                {{ contentModel.value }}
              </a>
            </td>
          </template>

          <template v-else>
            <ak-popover
              theme="dark"
              placement="top"
              width="325px"
              size="normal"
            >
              <template #trigger>
                <td
                  align="center"
                  class="block"
                  :bgcolor="contentModel.style.color"
                  role="presentation"
                  :style="contentStyles"
                  valign="middle"
                >
                  <a
                    ref="button"
                    contenteditable
                    :style="anchorStyles"
                    @blur="onEdit"
                    @keydown.enter="endEdit"
                    v-text="contentModel.value"
                  />
                </td>
              </template>
              <template #popover>
                <ak-form>
                  <ak-field label="Link" has-dark-bg class="mb-small">
                    <ak-input v-model="contentModel.href" />
                  </ak-field>
                  <ak-pack>
                    <ak-pack-item grow>
                      <ak-field label="Background" has-dark-bg class="mb-small">
                        <ak-color-picker
                          :color="contentModel.style.color"
                          @change-color="changeColor"
                        />
                      </ak-field>
                    </ak-pack-item>
                    <ak-pack-item grow>
                      <ak-field label="Text" has-dark-bg class="mb-small">
                        <ak-color-picker
                          :color="contentModel.style.textColor"
                          @change-color="changeTextColor"
                        />
                      </ak-field>
                    </ak-pack-item>
                  </ak-pack>
                  <ak-pack>
                    <ak-pack-item grow>
                      <ak-field label="Size" has-dark-bg class="mb-small">
                        <ak-range
                          v-model:value="contentModel.style.size"
                          :min="5"
                          :max="20"
                          :show-bubbles="false"
                        />
                      </ak-field>
                    </ak-pack-item>
                    <ak-pack-item grow>
                      <ak-field label="Roundness" has-dark-bg class="mb-small">
                        <ak-range
                          v-model:value="contentModel.style.borderRadius"
                          :min="0"
                          :max="30"
                          :show-bubbles="false"
                        />
                      </ak-field>
                    </ak-pack-item>
                  </ak-pack>
                  <ak-field label="Alignment" has-dark-bg class="mb-none">
                    <ak-radio-group
                      v-model="contentModel.style.alignment"
                      :options="alignOpts"
                      name="buttonAlignment"
                    >
                    </ak-radio-group>
                  </ak-field>
                </ak-form>
              </template>
            </ak-popover>
          </template>
        </tr>
      </table>
    </td>
  </tr>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Popover from '@akerolabs/akero-vue/src/components/popover/Popover.vue'
import ColorPicker from '@akerolabs/akero-vue/src/components/colorpicker/ColorPicker.vue'
import config from '../../utils/config'
import { rgbaToString } from '@akerolabs/akero-vue/src/utils/helpers'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-button></ak-email-template-button>
   */
  name: 'AkEmailTemplateButton',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Popover.name]: Popover,
    [Field.name]: Field,
    [Input.name]: Input,
    [ColorPicker.name]: ColorPicker,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The content to render/edit
     */
    modelValue: {
      type: Object,
      default: () => ({
        type: 'button',
        value: config.defaultButtonText,
        href: config.defaultButtonLink,
        target: config.defaultButtonTarget,
        style: {
          borderRadius: 10,
          textColor: '#ffffff',
          color: '#353535',
          alignment: 'left',
          size: 12,
        },
      }),
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    settings: {
      type: Object,
      default: () => ({ ...config.defaultTemplateStyles }),
    },

    /**
     * @type {Boolean}
     * @description Determines if we are to render the block for the
     * builder component (drag and drop editor)
     */
    isBuilder: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the content item is the last in its column
     */
    isLast: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Has flush spacing on the block
     */
    isFlush: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number}
     * @description Block index
     */
    blockIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description Content index
     */
    contentIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description The width of the column
     */
    width: {
      type: Number,
      default: null,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const contentModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    const alignOpts = ref(config.alignmentOptions)
    const button = ref(null)

    /**
     * @returns {Object}
     * @description returns the root styles for the component
     */
    const rootStyles = computed(() => {
      return {
        fontSize: '0px',
        padding: '0',
        wordBreak: 'break-word',
        paddingTop:
          !props.isBuilder && props.contentIndex === 0 && !props.isFlush
            ? '10px'
            : '0',
        paddingBottom: props.isBuilder || props.isLast ? '0' : '10px',
      }
    })

    /**
     * @returns {Object}
     * @description returns the content styles for the component
     */
    const contentStyles = computed(() => {
      return {
        cursor: 'auto',
        borderRadius: `${contentModel.value.style.borderRadius}px`,
        msoPaddingAlt: `${contentModel.value.style.size}px ${
          contentModel.value.style.size * 2.5
        }px`,
        background: contentModel.value.style.color,
      }
    })

    /**
     * @returns {Object}
     * @description returns the anchor styles for the component
     */
    const anchorStyles = computed(() => {
      return {
        display: 'inline-block',
        background: contentModel.value.style.color,
        color: contentModel.value.style.textColor,
        fontFamily: `${props.settings.bodyFont},sans-serif`,
        fontSize: '14px',
        fontWeight: 'bold',
        msoLineHeight: 'exactly',
        lineHeight: '1.15',
        margin: 0,
        textDecoration: 'none',
        textTransform: 'none',
        padding: `${contentModel.value.style.size}px ${
          contentModel.value.style.size * 2.5
        }px`,
        msoPaddingAlt: '0px',
        borderRadius: `${contentModel.value.style.borderRadius}px`,
      }
    })

    const changeColor = (color) => {
      contentModel.value.style.color = rgbaToString(color)
    }

    const changeTextColor = (color) => {
      contentModel.value.style.textColor = rgbaToString(color)
    }

    const onEdit = (e) => {
      const src = e.target.innerText
      contentModel.value.value = src
    }

    const endEdit = () => {
      button.value.blur()
    }

    return {
      alignOpts,
      rootStyles,
      contentStyles,
      anchorStyles,
      changeColor,
      changeTextColor,
      onEdit,
      endEdit,
      contentModel,
    }
  },
})
</script>

<style lang="scss" scoped>
.email-button {
}
</style>
